<script setup>
import { ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

defineProps({
  data: {
    type: Object,
    default: () => ({ suggestions: [], generatedAt: null }),
  },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['refresh']);

const FORMAT_LABEL = {
  video_curto: 'Vídeo curto',
  foto: 'Foto',
  carrossel: 'Carrossel',
  live: 'Live',
  texto: 'Texto',
  story: 'Story',
};

const TONE_LABEL = {
  institucional: 'Institucional',
  popular: 'Popular',
  firme: 'Firme',
  empatico: 'Empático',
  tecnico: 'Técnico',
  humorado: 'Humorado',
};

const expanded = ref(new Set());
const toggle = i => {
  if (expanded.value.has(i)) expanded.value.delete(i);
  else expanded.value.add(i);
  expanded.value = new Set(expanded.value);
};
const isOpen = i => expanded.value.has(i);

const supportClass = v =>
  v >= 70 ? 'bg-emerald-100 text-emerald-700' : v >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
const riskClass = v =>
  v >= 70 ? 'bg-rose-100 text-rose-700' : v >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
const engagementClass = v =>
  v === 'alto' ? 'bg-emerald-100 text-emerald-700' : v === 'medio' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
</script>

<template>
  <div class="rounded-xl border border-n-strong/40 bg-n-background p-4 shadow-sm">
    <div class="mb-3 flex items-center justify-between">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">Sugestões de Conteúdo</h3>
        <p class="mt-0.5 text-[11px] text-n-slate-11">
          O que postar a seguir, baseado em dados.
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-sparkles"
        :is-loading="loading"
        :disabled="disabled"
        @click="emit('refresh')"
      >
        Gerar
      </Button>
    </div>

    <div v-if="disabled" class="py-6 text-center text-sm text-n-slate-11">
      Selecione um perfil para gerar sugestões.
    </div>

    <div v-else-if="loading && !data.suggestions?.length" class="flex justify-center py-8">
      <Spinner />
    </div>

    <div
      v-else-if="!data.suggestions?.length"
      class="py-6 text-center text-sm text-n-slate-11"
    >
      Clique em "Gerar" para receber sugestões para os próximos 7 dias.
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="(s, i) in data.suggestions"
        :key="i"
        class="rounded-lg border border-n-weak bg-n-background p-3 transition hover:border-n-strong/50"
      >
        <div class="mb-2 flex items-start gap-2">
          <span class="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-[11px] font-bold text-blue-700">
            {{ i + 1 }}
          </span>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-bold text-n-slate-12">{{ s.theme }}</p>
            <div class="mt-1 flex flex-wrap items-center gap-1.5 text-[10px]">
              <span class="rounded bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700">
                {{ FORMAT_LABEL[s.format] || s.format }}
              </span>
              <span class="rounded bg-purple-50 px-1.5 py-0.5 font-medium text-purple-700">
                {{ TONE_LABEL[s.tone] || s.tone }}
              </span>
              <span class="rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700">
                ⏰ {{ s.bestTime }}
              </span>
              <span :class="engagementClass(s.expectedEngagement)" class="rounded px-1.5 py-0.5 font-medium">
                Eng: {{ s.expectedEngagement }}
              </span>
              <span :class="supportClass(s.politicalSupport)" class="rounded px-1.5 py-0.5 font-medium">
                Apoio {{ s.politicalSupport }}
              </span>
              <span :class="riskClass(s.rejectionRisk)" class="rounded px-1.5 py-0.5 font-medium">
                Risco {{ s.rejectionRisk }}
              </span>
            </div>
          </div>
          <button
            type="button"
            class="text-n-slate-11 hover:text-n-slate-12"
            @click="toggle(i)"
          >
            <span :class="isOpen(i) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-4" />
          </button>
        </div>

        <div v-if="isOpen(i)" class="space-y-2 border-t border-n-weak pt-2">
          <div class="rounded-md bg-blue-50 p-2 text-[11px] text-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
            <span class="font-semibold">Gancho: </span>{{ s.narrativeHook }}
          </div>
          <div class="rounded-md bg-emerald-50 p-2 text-[11px] text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
            <span class="font-semibold">CTA: </span>{{ s.cta }}
          </div>
          <p class="text-[11px] italic text-n-slate-11">
            <span class="font-semibold">Por quê: </span>{{ s.rationale }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
